/**
 * BookmarkContext
 *
 * React context for bookmark state management.
 * Integrates with SupabaseBookmarkOrchestrator for database-backed bookmarks
 * with localStorage fallback for unauthenticated users.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import SupabaseBookmarkOrchestrator from '../services/supabaseBookmark/SupabaseBookmarkOrchestrator';

const BookmarkContext = createContext(null);

export const BookmarkProvider = ({ children }) => {
  const { isAuthenticated, user, isLoading: authLoading } = useAuth0();

  const [bookmarkedIds, setBookmarkedIds] = useState([]);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Get orchestrator instance
  const orchestrator = useMemo(() => SupabaseBookmarkOrchestrator.getInstance(), []);

  // Initialize orchestrator when auth state is ready
  useEffect(() => {
    if (authLoading) return;

    const initialize = async () => {
      setIsLoading(true);

      const userId = isAuthenticated ? user?.sub : null;
      await orchestrator.initialize(isAuthenticated, userId);

      const state = orchestrator.getState();
      setBookmarkedIds(state.bookmarkedIds);
      setCount(state.count);
      setIsInitialized(true);
      setIsLoading(false);
    };

    initialize();
  }, [authLoading, isAuthenticated, user?.sub, orchestrator]);

  // Handle auth state changes (login/logout)
  useEffect(() => {
    if (!isInitialized || authLoading) return;

    const handleAuthChange = async () => {
      const userId = isAuthenticated ? user?.sub : null;
      await orchestrator.switchStorageMode(isAuthenticated, userId);
    };

    handleAuthChange();
  }, [isAuthenticated, user?.sub, isInitialized, authLoading, orchestrator]);

  // Subscribe to orchestrator state changes
  useEffect(() => {
    const unsubscribe = orchestrator.subscribe((state) => {
      setBookmarkedIds(state.bookmarkedIds);
      setCount(state.count);
      setIsLoading(state.isLoading);
    });

    return unsubscribe;
  }, [orchestrator]);

  // Toggle bookmark for a flashcard
  const toggleBookmark = useCallback(async (flashcardId) => {
    const result = await orchestrator.toggleBookmark(flashcardId);
    return result;
  }, [orchestrator]);

  // Check if a flashcard is bookmarked
  const isBookmarked = useCallback((flashcardId) => {
    return orchestrator.isBookmarked(flashcardId);
  }, [orchestrator]);

  // Get bookmarked flashcards from a list
  const getBookmarkedFlashcards = useCallback((flashcards) => {
    return orchestrator.getBookmarkedFlashcards(flashcards);
  }, [orchestrator]);

  // Get all bookmark IDs
  const getBookmarks = useCallback(() => {
    return orchestrator.getBookmarks();
  }, [orchestrator]);

  // Clear all bookmarks
  const clearAllBookmarks = useCallback(async () => {
    return await orchestrator.clearAllBookmarks();
  }, [orchestrator]);

  const value = useMemo(() => ({
    // State
    bookmarkedIds,
    count,
    isLoading,
    isInitialized,

    // Methods
    toggleBookmark,
    isBookmarked,
    getBookmarkedFlashcards,
    getBookmarks,
    clearAllBookmarks
  }), [
    bookmarkedIds,
    count,
    isLoading,
    isInitialized,
    toggleBookmark,
    isBookmarked,
    getBookmarkedFlashcards,
    getBookmarks,
    clearAllBookmarks
  ]);

  return (
    <BookmarkContext.Provider value={value}>
      {children}
    </BookmarkContext.Provider>
  );
};

export const useBookmarks = () => {
  const context = useContext(BookmarkContext);

  if (!context) {
    throw new Error('useBookmarks must be used within a BookmarkProvider');
  }

  return context;
};

export default BookmarkContext;
